// Box card — shows one SMTP box's status, health, and key metrics

import { clsx } from 'clsx'
import { Pause, Play, SlidersHorizontal } from 'lucide-react'
import { StatusBadge } from '@/components/ui/status-badge'
import type { SmtpBox } from '@/lib/types-boxes'

interface BoxCardProps {
  box: SmtpBox
  onSelect: (id: string) => void
  onPause: (id: string) => void
  onResume: (id: string) => void
  onAdjustWeight: (id: string) => void
  isPausing: boolean
  isResuming: boolean
}

function healthColor(score: number): string {
  if (score >= 80) return 'bg-green-500'
  if (score >= 50) return 'bg-yellow-500'
  if (score >= 20) return 'bg-orange-500'
  return 'bg-red-500'
}

function healthBgColor(score: number): string {
  if (score >= 80) return 'bg-green-100'
  if (score >= 50) return 'bg-yellow-100'
  if (score >= 20) return 'bg-orange-100'
  return 'bg-red-100'
}

function formatRate(rate: string | number): string {
  const num = typeof rate === 'string' ? Number.parseFloat(rate) : rate
  return `${(num * 100).toFixed(2)}%`
}

function warmingLabel(box: SmtpBox): string {
  if (box.status === 'warm') return 'Complete'
  if (box.warming_day > 0) return `Day ${box.warming_day}/30`
  return 'Not started'
}

export function BoxCard({
  box,
  onSelect,
  onPause,
  onResume,
  onAdjustWeight,
  isPausing,
  isResuming,
}: BoxCardProps): React.JSX.Element {
  return (
    // biome-ignore lint/a11y/useSemanticElements: contains nested buttons, can't use <button> wrapper
    <div
      role="button"
      tabIndex={0}
      className="rounded-xl border border-surface-3 bg-surface-0 p-5 shadow-sm transition-shadow hover:shadow-md cursor-pointer"
      onClick={() => onSelect(box.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect(box.id)
        }
      }}
    >
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-text-primary">{box.hostname}</h3>
          <StatusBadge status={box.status} />
        </div>
        <span className="text-xs text-text-tertiary">Weight: {box.weight}%</span>
      </div>

      {/* Health bar */}
      <div className="mt-3">
        <div className="flex items-center justify-between text-xs">
          <span className="text-text-secondary">Health</span>
          <span className="font-medium text-text-primary">{box.health_score}/100</span>
        </div>
        <div className={clsx('mt-1 h-2 w-full rounded-full', healthBgColor(box.health_score))}>
          <div
            className={clsx('h-2 rounded-full transition-all', healthColor(box.health_score))}
            style={{ width: `${box.health_score}%` }}
          />
        </div>
      </div>

      {/* Metrics row */}
      <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
        <div>
          <p className="text-text-tertiary">Vol/day</p>
          <p className="font-medium text-text-primary">{box.volume_24h.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-text-tertiary">Bounce</p>
          <p className="font-medium text-text-primary">{formatRate(box.bounce_rate_24h)}</p>
        </div>
        <div>
          <p className="text-text-tertiary">Complaint</p>
          <p className="font-medium text-text-primary">{formatRate(box.complaint_rate_24h)}</p>
        </div>
        <div>
          <p className="text-text-tertiary">Deferral</p>
          <p className="font-medium text-text-primary">{formatRate(box.deferral_rate_1h)}</p>
        </div>
      </div>

      {/* Footer: IP, warming, spamhaus */}
      <div className="mt-3 flex items-center justify-between text-xs text-text-tertiary">
        <span>IP: {box.ip_address}</span>
        <span>Warming: {warmingLabel(box)}</span>
        <span className={box.spamhaus_clean ? 'text-green-600' : 'text-red-600'}>
          {box.spamhaus_clean ? 'Clean' : 'Listed'}
        </span>
      </div>

      {/* Actions */}
      {/* biome-ignore lint/a11y/useSemanticElements: no semantic equivalent for role="group" in this context */}
      <div
        role="group"
        className="mt-3 flex gap-2 border-t border-surface-3 pt-3"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => onAdjustWeight(box.id)}
          className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-1"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Adjust Weight
        </button>
        {box.status !== 'paused' ? (
          <button
            type="button"
            onClick={() => onPause(box.id)}
            disabled={isPausing}
            className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-yellow-700 hover:bg-yellow-50 disabled:opacity-50"
          >
            <Pause className="h-3.5 w-3.5" />
            {isPausing ? 'Pausing...' : 'Pause'}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onResume(box.id)}
            disabled={isResuming}
            className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-green-700 hover:bg-green-50 disabled:opacity-50"
          >
            <Play className="h-3.5 w-3.5" />
            {isResuming ? 'Resuming...' : 'Resume'}
          </button>
        )}
      </div>
    </div>
  )
}
